/**
 * operationalMotionTiming
 *
 * Deterministic semantic timing groups for Governance Workspace motion.
 * All durations are fixed constants — no randomness, no runtime derivation.
 *
 * Rule: Motion duration is bounded and deterministic.
 * Rule: No spring, bounce, or elastic easing.
 * Rule: Reduced-motion mode overrides all durations to 0ms or instant.
 */

/** Selection choreography — fast, subtle elevation */
export const SELECTION_TIMING = {
  /** Background color transition on row/node selection */
  backgroundMs: 120,
  /** Border/indicator accent transition */
  accentMs: 160,
  /** Inspector content fade/slide on selection change */
  inspectorContentMs: 140,
  /** Synchronized highlight propagation between panes */
  syncPropagationMs: 80,
  easing: 'ease-out',
} as const;

/** Continuity transitions — medium, perceivable but calm */
export const CONTINUITY_TIMING = {
  /** Degraded → stabilized state pulse */
  statePulseMs: 320,
  /** Overlay fade in/out */
  overlayFadeMs: 220,
  /** Refusal marker settle */
  refusalSettleMs: 280,
  /** Continuity dot appearance */
  dotAppearMs: 180,
  easing: 'ease-in-out',
} as const;

/** Replay semantics — deterministic by window, not by animation curve */
export const REPLAY_TIMING = {
  /** Replay cursor movement interpolation */
  cursorMoveMs: 200,
  /** Replay window change preserve spatial continuity */
  windowTransitionMs: 260,
  /** Causal progression emphasis */
  causalEmphasisMs: 180,
  easing: 'ease-out',
} as const;

/** Verification surfaces — minimal, settle-only */
export const VERIFICATION_TIMING = {
  /** VERIFIED badge fade-in */
  validFadeMs: 100,
  /** INVALID badge sharp transition */
  invalidTransitionMs: 80,
  /** proofOnly badge subtle transition */
  proofOnlyMs: 120,
  easing: 'ease-out',
} as const;

/** Graph choreography — smooth but not theatrical */
export const GRAPH_TIMING = {
  /** Node selection stroke transition */
  nodeSelectMs: 140,
  /** Edge emphasis when selected */
  edgeEmphasisMs: 160,
  /** Hover capsule fade */
  capsuleFadeMs: 100,
  /** Minimap viewport sync */
  minimapSyncMs: 200,
  /** Graph focus center transition */
  focusCenterMs: 280,
  easing: 'ease-out',
} as const;

/** Structural transitions — collapse, expand, layout */
export const STRUCTURAL_TIMING = {
  /** Collapse/expand panel sections */
  collapseMs: 120,
  /** Tab switch content */
  tabSwitchMs: 100,
  /** Pane visibility toggle */
  paneVisibilityMs: 160,
  easing: 'ease-out',
} as const;

/** Global motion timing reference */
export const OPERATIONAL_MOTION_TIMING = {
  selection: SELECTION_TIMING,
  continuity: CONTINUITY_TIMING,
  replay: REPLAY_TIMING,
  verification: VERIFICATION_TIMING,
  graph: GRAPH_TIMING,
  structural: STRUCTURAL_TIMING,
} as const;
